/**
 * Scryfall API utility with rate limiting
 * Scryfall allows ~10 requests/second, we'll be conservative with batches of 8 every 100ms
 */

import API_URL, { R2_IMAGE_URL } from '../config';

/**
 * TCGPlayer affiliate link configuration
 */
const TCGPLAYER_AFFILIATE_BASE = 'https://tcgplayer.pxf.io/c/5745026/1780961/21018';
const TCGPLAYER_PARTNER_ID = '6346843';

/**
 * Generate a TCGPlayer affiliate search URL for a single card
 * @param {string} cardName - The card name to search for
 * @param {string} origin - Origin identifier for tracking (e.g., 'Manabase', 'DeckBuilder', 'SelectedCards')
 * @returns {string} - The TCGPlayer affiliate URL
 */
export const getTcgPlayerUrl = (cardName, origin = 'Card') => {
  const searchQuery = encodeURIComponent(cardName);
  const subId = `EDHBuild_Card${origin}`;
  const targetUrl = `https://www.tcgplayer.com/search/magic/product?productLineName=magic&q=${searchQuery}&view=grid`;
  const encodedTargetUrl = encodeURIComponent(targetUrl);
  return `${TCGPLAYER_AFFILIATE_BASE}?subId1=${subId}&partnerpropertyid=${TCGPLAYER_PARTNER_ID}&u=${encodedTargetUrl}`;
};

/**
 * Open a TCGPlayer search for a card in a new tab
 * @param {string} cardName - The card name to search for
 * @param {string} origin - Origin identifier for tracking
 */
export const openTcgPlayerCard = (cardName, origin = 'Card') => {
  window.open(getTcgPlayerUrl(cardName, origin), '_blank');
};

/**
 * Sanitize card name for R2 filename lookup
 * Must match the sanitizeFilename function in scripts/populate-images/download.go
 */
export const sanitizeFilename = (name) => {
  return name
    // Normalize curly apostrophes to straight
    .replace(/[\u2018\u2019\u2032]/g, "'")
    .replace(/\//g, '-')
    .replace(/\\/g, '-')
    .replace(/:/g, '-')
    .replace(/"/g, '')
    .replace(/\?/g, '')
    .replace(/\*/g, '')
    .replace(/</g, '')
    .replace(/>/g, '')
    .replace(/\|/g, '');
};

/**
 * Get R2 CDN URL for a card image
 * @param {string} cardName - The card name
 * @returns {string} - The R2 URL for the card image
 */
export const getR2ImageUrl = (cardName) => {
  const sanitized = sanitizeFilename(cardName);
  // Encode the filename for URL, then also encode apostrophes which encodeURIComponent doesn't handle
  // (apostrophes break CSS url() if not encoded)
  return `${R2_IMAGE_URL}/cards/${encodeURIComponent(sanitized).replace(/'/g, '%27')}.jpg`;
};

/**
 * Probe a card's R2 image and, ONLY if it is missing (R2 doesn't have it yet),
 * invoke onMissing with a Scryfall image-CDN URL (cards.scryfall.io).
 *
 * This exists because most card displays use CSS `background-image`, which has no
 * `onError` event — so a missing R2 image silently shows nothing. By loading the R2
 * URL through an Image() probe we can detect the miss and let the caller swap to
 * Scryfall. For cards that ARE in R2 the probe loads from the browser's HTTP cache
 * (same URL the background-image uses), so it does not add a second network request.
 *
 * IMPORTANT: we resolve to the Scryfall image CDN (image_uris.normal), NOT the
 * `api.scryfall.com/cards/named?...&format=image` redirect. That redirect is
 * rate-limited and bulk use of it triggers 429 storms. Callers that already know the
 * CDN URL (from card metadata) should pass it as `scryfallFallback`; if they don't,
 * we look it up once via the rate-limited request queue.
 *
 * @param {string} cardName - The card name
 * @param {string|null} scryfallFallback - Known Scryfall image-CDN URL (image_uris.normal); looked up if falsy
 * @param {(url: string) => void} onMissing - Called with the CDN URL when R2 is missing the image
 */
export const fallbackIfR2Missing = (cardName, scryfallFallback, onMissing) => {
  const probe = new Image();
  probe.onerror = async () => {
    let url = scryfallFallback;
    if (!url) {
      // No CDN URL known — fetch metadata (rate-limited + bounded) to get image_uris.
      try {
        const data = await fetchCardByExactName(cardName);
        if (data && !data._error) {
          url = data.image_uris?.normal || data.card_faces?.[0]?.image_uris?.normal || '';
        }
      } catch { /* ignore — just won't show a fallback image */ }
    }
    if (url) onMissing(url);
  };
  probe.src = getR2ImageUrl(cardName);
};

/**
 * Check if an image exists at a URL (HEAD request)
 * @param {string} url - URL to check
 * @returns {Promise<boolean>} - True if image exists
 */
export const checkImageExists = async (url) => {
  try {
    const response = await fetch(url, { method: 'HEAD' });
    return response.ok;
  } catch {
    return false;
  }
};

/**
 * Request the backend to cache a card image to R2 (fire-and-forget)
 * This is called when we fall back to Scryfall, so future requests will hit R2
 * @param {string} cardName - The card name to cache
 */
export const requestImageCache = (cardName) => {
  // Fire-and-forget - don't await, don't handle errors (it's a background optimization)
  fetch(`${API_URL}/v1/cache-image`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ cardName })
  }).catch(() => {}); // Silently ignore errors
};

/**
 * Batch fetch multiple cards using Scryfall's collection endpoint
 * Much more efficient than individual requests - fetches up to 75 cards in one request
 * @param {string[]} cardNames - Array of card names to fetch
 * @returns {Promise<Object>} - Map of cardName -> card data
 */
export const fetchCardsBatch = async (cardNames) => {
  const startTime = performance.now();
  const log = (msg) => console.log(`[fetchCardsBatch ${(performance.now() - startTime).toFixed(0)}ms] ${msg}`);
  
  if (!cardNames || cardNames.length === 0) return {};
  
  log(`Starting with ${cardNames.length} cards`);
  
  // Scryfall collection endpoint accepts up to 75 identifiers per request
  const batchSize = 75;
  const results = {};
  const numBatches = Math.ceil(cardNames.length / batchSize);
  log(`Will make ${numBatches} batch request(s)`);

  // Cap 429 retries per batch so a persistent rate-limit can't loop forever.
  const MAX_RATE_LIMIT_RETRIES = 5;
  let rateLimitRetries = 0;

  for (let i = 0; i < cardNames.length; i += batchSize) {
    const batchNum = Math.floor(i / batchSize) + 1;
    const batch = cardNames.slice(i, i + batchSize);
    const identifiers = batch.map(name => ({ name }));

    log(`Batch ${batchNum}/${numBatches}: Sending ${batch.length} cards to Scryfall...`);

    try {
      const fetchStart = performance.now();
      const response = await fetch('https://api.scryfall.com/cards/collection', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ identifiers })
      });
      log(`Batch ${batchNum}: HTTP response in ${(performance.now() - fetchStart).toFixed(0)}ms, status ${response.status}`);

      if (response.status === 429) {
        if (rateLimitRetries >= MAX_RATE_LIMIT_RETRIES) {
          log(`Batch ${batchNum}: Still rate limited after ${rateLimitRetries} retries, skipping (partial results)`);
          rateLimitRetries = 0;
          continue; // give up on this batch and move on instead of looping forever
        }
        rateLimitRetries++;
        const wait = Math.min(1000 * 2 ** (rateLimitRetries - 1), 8000); // exponential backoff, capped at 8s
        log(`Batch ${batchNum}: Rate limited! Backing off ${wait}ms (retry ${rateLimitRetries}/${MAX_RATE_LIMIT_RETRIES})...`);
        await new Promise(r => setTimeout(r, wait));
        i -= batchSize; // Retry this batch
        continue;
      }
      rateLimitRetries = 0; // reset once we get a non-429 response

      const parseStart = performance.now();
      const data = await response.json();
      log(`Batch ${batchNum}: JSON parsed in ${(performance.now() - parseStart).toFixed(0)}ms, got ${data.data?.length || 0} cards`);
      
      if (data.data) {
        for (const card of data.data) {
          results[card.name] = card;
        }
      }
      
      // Small delay between batches to be nice to Scryfall
      if (i + batchSize < cardNames.length) {
        await new Promise(r => setTimeout(r, 100));
      }
    } catch (error) {
      console.error('Error in batch fetch:', error);
    }
  }
  
  log(`Done! Returning ${Object.keys(results).length} cards`);
  return results;
};

// Queue for pending requests
let requestQueue = [];
let isProcessing = false;

// Promise-based delay helper.
const sleep = (ms) => new Promise(r => setTimeout(r, ms));

// Max times a single request will be re-queued after a 429 before we give up,
// so a persistent rate-limit can't spin this queue (and its callers) forever.
const MAX_RATE_LIMIT_RETRIES = 5;

// Run one queued request: resolve/reject the caller's promise, and report whether
// it was rate-limited (429) so the queue can back off before the next batch.
const runQueuedRequest = (item) => {
  const { url, resolve, reject } = item;
  return fetch(url)
    .then(async (response) => {
      if (response.status === 429) {
        item.retries = (item.retries || 0) + 1;
        if (item.retries <= MAX_RATE_LIMIT_RETRIES) {
          // Requeue (at the back); the queue backs off before the next batch.
          requestQueue.push(item);
          return true;
        }
        // Give up after too many retries so the awaiting caller resolves instead
        // of hanging forever. Use the same error shape as other error responses.
        resolve({ object: 'error', _error: true, _status: 429 });
        return true;
      }
      const data = await response.json();
      // Handle Scryfall error responses (404, 403, etc) without throwing.
      if (!response.ok || data.object === 'error') {
        resolve({ ...data, _error: true, _status: response.status });
      } else {
        resolve(data);
      }
      return false;
    })
    .catch((err) => { reject(err); return false; });
};

// Process queue with rate limiting
const processQueue = async () => {
  if (isProcessing || requestQueue.length === 0) return;

  isProcessing = true;
  let backoff = 120;

  while (requestQueue.length > 0) {
    // Take up to 8 requests at a time
    const batch = requestQueue.splice(0, 8);
    const outcomes = await Promise.all(batch.map(runQueuedRequest));
    const rateLimited = outcomes.some(Boolean);

    if (requestQueue.length > 0) {
      // Back off exponentially while rate limited; otherwise keep normal pacing.
      backoff = rateLimited ? Math.min(backoff * 2, 5000) : 120;
      await sleep(backoff);
    }
  }

  isProcessing = false;
};

/**
 * Rate-limited fetch from Scryfall API
 * @param {string} url - Full Scryfall API URL
 * @returns {Promise<Object>} - Parsed JSON response
 */
export const scryfallFetch = (url) => {
  return new Promise((resolve, reject) => {
    requestQueue.push({ url, resolve, reject });
    processQueue();
  });
};

/**
 * Fetch card by name (fuzzy match)
 * @param {string} cardName - Card name to search for
 * @returns {Promise<Object>} - Card data from Scryfall
 */
export const fetchCardByName = async (cardName) => {
  const url = `https://api.scryfall.com/cards/named?fuzzy=${encodeURIComponent(cardName)}`;
  return scryfallFetch(url);
};

/**
 * Fetch card by exact name
 * @param {string} cardName - Exact card name
 * @returns {Promise<Object>} - Card data from Scryfall
 */
export const fetchCardByExactName = async (cardName) => {
  const url = `https://api.scryfall.com/cards/named?exact=${encodeURIComponent(cardName)}`;
  return scryfallFetch(url);
};

/**
 * Search for cards
 * @param {string} query - Search query
 * @returns {Promise<Object>} - Search results from Scryfall
 */
export const searchCards = async (query) => {
  const url = `https://api.scryfall.com/cards/search?q=${encodeURIComponent(query)}&format=json`;
  return scryfallFetch(url);
};

/**
 * Autocomplete card names (not rate limited as heavily, but still use queue)
 * @param {string} query - Partial card name
 * @returns {Promise<Object>} - Autocomplete results
 */
export const autocompleteCards = async (query) => {
  const url = `https://api.scryfall.com/cards/autocomplete?q=${encodeURIComponent(query)}`;
  return scryfallFetch(url);
};

/**
 * Fetch all printings of a card and find the cheapest one with a price
 * @param {string} cardName - Card name to search for
 * @returns {Promise<Object|null>} - Cheapest printing with price, or null if none found
 */
export const fetchCheapestPrinting = async (cardName) => {
  try {
    // Search for all printings of this card (unique prints only)
    const query = `!"${cardName}" unique:prints`;
    const url = `https://api.scryfall.com/cards/search?q=${encodeURIComponent(query)}&order=usd&dir=asc`;
    const results = await scryfallFetch(url);
    
    if (!results.data || results.data.length === 0) {
      return null;
    }
    
    // Find the cheapest printing that has a USD price
    for (const card of results.data) {
      const price = card.prices?.usd || card.prices?.usd_foil;
      if (price) {
        return {
          price: price,
          set: card.set?.toUpperCase(),
          set_name: card.set_name
        };
      }
    }
    
    return null;
  } catch (error) {
    console.error('Error fetching cheapest printing for', cardName, error);
    return null;
  }
};

/**
 * Parse card data into our app's format
 * @param {Object} data - Raw Scryfall card data
 * @param {string} [r2ImageUrl] - Optional R2 CDN image URL to use instead of Scryfall
 * @returns {Object} - Normalized card data for our app
 */
export const parseCardData = (data, r2ImageUrl = null) => {
  // Check if it's a double-faced card
  const isDFC = data.card_faces && data.card_faces.length > 1 && data.card_faces[0].image_uris;
  
  // Use R2 URL for single-faced cards if provided, otherwise use Scryfall
  let imageUrl;
  if (r2ImageUrl && !isDFC) {
    imageUrl = r2ImageUrl;
  } else if (isDFC) {
    imageUrl = data.card_faces[0].image_uris.normal;
  } else {
    imageUrl = data.image_uris?.normal || data.card_faces?.[0]?.image_uris?.normal || '';
  }
  
  return {
    name: data.name,
    imageUrl,
    backImageUrl: isDFC ? (data.card_faces[1].image_uris?.normal || null) : null,
    cmc: data.cmc || 0,
    type_line: data.type_line || '',
    colors: data.colors || [],
    color_identity: data.color_identity || [],
    mana_cost: data.mana_cost || '',
    rarity: data.rarity || 'common',
    artist: data.artist || 'Unknown',
    set_name: data.set_name || 'Unknown',
    price: data.prices?.usd ? parseFloat(data.prices.usd) : null,
    legalities: data.legalities || {}
  };
};

/**
 * Fetch card by name and parse with R2 CDN image support
 * @param {string} cardName - Card name to fetch
 * @returns {Promise<Object>} - Parsed card data with R2 image when available
 */
export const fetchCardWithR2Image = async (cardName) => {
  const data = await fetchCardByName(cardName);
  
  // Try R2 first for non-DFC cards
  const isDFC = data.card_faces && data.card_faces.length > 1 && data.card_faces[0].image_uris;
  let r2ImageUrl = null;
  
  if (!isDFC) {
    const r2Url = getR2ImageUrl(cardName);
    const r2Available = await checkImageExists(r2Url);
    if (r2Available) {
      r2ImageUrl = r2Url;
    } else {
      // Request backend to cache this image for future users
      requestImageCache(cardName);
    }
  }
  
  return parseCardData(data, r2ImageUrl);
};
